import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Bar, Line } from 'react-chartjs-2';
import { 
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import toast from 'react-hot-toast';
import { useLanguage } from '../../context/LanguageContext';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Filler
);

const D6Verification = ({ selectedEightD }) => {
  const { t, language } = useLanguage();
  
  // Función para traducir conclusiones del backend
  const translateConclusion = useCallback((conclusion) => {
    if (!conclusion) return '';
    
    const conclusionMap = {
      'No concluyente': language === 'es' ? 'No concluyente' : 'Inconclusive',
      'No hay evidencia estadística de mejora': language === 'es' 
        ? 'No hay evidencia estadística de mejora' 
        : 'No statistical evidence of improvement',
      'Mejora estadísticamente significativa confirmada': language === 'es'
        ? 'Mejora estadísticamente significativa confirmada'
        : 'Statistically significant improvement confirmed',
      'Empeoramiento estadísticamente significativo confirmado': language === 'es'
        ? 'Empeoramiento estadísticamente significativo confirmado'
        : 'Statistically significant worsening confirmed',
      'Evidencia mixta - requiere más análisis': language === 'es'
        ? 'Evidencia mixta - requiere más análisis'
        : 'Mixed evidence - requires further analysis'
    };
    
    return conclusionMap[conclusion] || conclusion;
  }, [language]);
  
  // Estados
  const [measurements, setMeasurements] = useState([]);
  const [analysisWindows, setAnalysisWindows] = useState([]);
  const [statisticalSummary, setStatisticalSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPostForm, setShowPostForm] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [postForm, setPostForm] = useState({
    period_end: new Date().toISOString().split('T')[0],
    compared_to_measurement_id: '',
    notes: ''
  });

  // Cargar mediciones cuando se selecciona un 8D
  const loadMeasurements = useCallback(async () => {
    if (!selectedEightD?.problem?.id) return;
    
    try {
      setLoading(true);
      const { data } = await axios.get(`/measurements/problem/${selectedEightD.problem.id}`);
      setMeasurements(data.measurements || []);
      
      // Configurar la PRE como comparación por defecto si existe
      const preMeasurement = data.measurements?.find(m => m.measurement_type === 'PRE');
      if (preMeasurement && !postForm.compared_to_measurement_id) {
        setPostForm(prev => ({ ...prev, compared_to_measurement_id: preMeasurement.id }));
      }
    } catch (error) {
      console.error('Error loading measurements:', error);
      toast.error(t('d6verification.messages.loadError'));
    } finally {
      setLoading(false);
    }
  }, [selectedEightD?.problem?.id, postForm.compared_to_measurement_id, t]);

  useEffect(() => {
    if (selectedEightD?.problem?.id) {
      loadMeasurements();
    } else {
      setMeasurements([]);
    }
  }, [selectedEightD?.problem?.id, loadMeasurements]);

  const createPostMeasurement = async () => {
    if (!selectedEightD?.problem?.id) {
      toast.error(t('d6verification.messages.noProblem'));
      return;
    }

    if (!postForm.period_end) {
      toast.error(t('d6verification.messages.selectEndDate'));
      return;
    }

    try {
      setLoading(true);
      await axios.post('/measurements/post', {
        problem_id: selectedEightD.problem.id,
        period_end: postForm.period_end,
        compared_to_measurement_id: postForm.compared_to_measurement_id || null,
        notes: postForm.notes || null
      });
      
      toast.success(t('d6verification.messages.postCreated'));
      setShowPostForm(false);
      setPostForm({
        period_end: new Date().toISOString().split('T')[0],
        compared_to_measurement_id: '',
        notes: ''
      });
      await loadMeasurements();
      await loadAnalysisWindows(); // Cargar análisis estadístico también
    } catch (error) {
      console.error('Error creating POST measurement:', error);
      toast.error(error.response?.data?.error || t('d6verification.messages.postCreateError'));
    } finally {
      setLoading(false);
    }
  };

  const loadAnalysisWindows = async () => {
    if (!selectedEightD?.problem?.id) return;
    
    try {
      const { data } = await axios.get(`/analysis-windows/problem/${selectedEightD.problem.id}`);
      setAnalysisWindows(data || []);
      
      // Cargar resumen estadístico si existe una ventana con POST
      const windowWithPost = data.find(w => w.post_mean !== null);
      if (windowWithPost) {
        loadStatisticalSummary(windowWithPost.id);
      }
    } catch (error) {
      console.error('Error loading analysis windows:', error);
    }
  };

  const loadStatisticalSummary = async (windowId) => {
    try {
      const { data } = await axios.get(`/analysis-windows/${windowId}/summary`);
      setStatisticalSummary(data);
    } catch (error) {
      console.error('Error loading statistical summary:', error);
    }
  };

  const createAnalysisWindow = async (measurementId) => {
    try {
      setLoading(true);
      await axios.post('/analysis-windows/from-measurement', {
        measurement_id: measurementId
      });
      toast.success(t('d6verification.messages.windowCreated'));
      await loadAnalysisWindows();
    } catch (error) {
      console.error('Error creating analysis window:', error);
      toast.error(error.response?.data?.message || t('d6verification.messages.windowCreateError'));
    } finally {
      setLoading(false);
    }
  };

  const updateWindowWithPost = async (windowId, postMeasurementId) => {
    try {
      setLoading(true);
      await axios.post(`/analysis-windows/${windowId}/update-post`, {
        post_measurement_id: postMeasurementId
      });
      toast.success(t('d6verification.messages.analysisCompleted'));
      await loadAnalysisWindows();
    } catch (error) {
      console.error('Error updating analysis window:', error);
      toast.error(error.response?.data?.message || t('d6verification.messages.analysisError'));
    } finally {
      setLoading(false);
    }
  };

  const recalculateMeasurement = async (measurementId) => {
    try {
      setLoading(true);
      await axios.post(`/measurements/${measurementId}/recalculate`);
      toast.success(t('d6verification.messages.recalculated'));
      await loadMeasurements();
    } catch (error) {
      console.error('Error recalculating:', error);
      toast.error(t('d6verification.messages.recalculateError'));
    } finally {
      setLoading(false);
    }
  };

  const deleteMeasurement = async (measurementId) => {
    if (!window.confirm(t('d6verification.messages.confirmDelete'))) return;
    
    try {
      setLoading(true);
      await axios.delete(`/measurements/${measurementId}`);
      toast.success(t('d6verification.messages.deleted'));
      await loadMeasurements();
    } catch (error) {
      console.error('Error deleting measurement:', error);
      toast.error(t('d6verification.messages.deleteError'));
    } finally {
      setLoading(false);
    }
  };

  if (!selectedEightD) {
    return null; // Consistente con otros módulos - no mostrar nada si no hay 8D seleccionado
  }

  // Extraer PRE y POST
  const preMeasurement = measurements.find(m => m.measurement_type === 'PRE');
  const postMeasurements = measurements.filter(m => m.measurement_type === 'POST').sort((a, b) => 
    new Date(a.period_end) - new Date(b.period_end)
  );

  // Preparar datos para gráficos
  const chartData = {
    labels: [
      preMeasurement ? 'PRE' : '',
      ...postMeasurements.map((m, i) => `POST ${i + 1}`)
    ].filter(Boolean),
    datasets: [
      {
        label: 'Tasa de Defectos (%)',
        data: [
          parseFloat(preMeasurement?.defect_rate || 0),
          ...postMeasurements.map(m => parseFloat(m.defect_rate || 0))
        ].filter((_, i) => i === 0 ? preMeasurement : true),
        backgroundColor: [
          'rgba(239, 68, 68, 0.7)',
          ...postMeasurements.map(() => 'rgba(34, 197, 94, 0.7)')
        ].filter((_, i) => i === 0 ? preMeasurement : true),
        borderColor: [
          'rgb(239, 68, 68)',
          ...postMeasurements.map(() => 'rgb(34, 197, 94)')
        ].filter((_, i) => i === 0 ? preMeasurement : true),
        borderWidth: 2
      }
    ]
  };

  const trendChartData = {
    labels: [
      preMeasurement?.period_end ? new Date(preMeasurement.period_end).toLocaleDateString() : '',
      ...postMeasurements.map(m => new Date(m.period_end).toLocaleDateString())
    ].filter(Boolean),
    datasets: [
      {
        label: 'Tasa de Defectos (%)',
        data: [
          parseFloat(preMeasurement?.defect_rate || 0),
          ...postMeasurements.map(m => parseFloat(m.defect_rate || 0))
        ].filter((_, i) => i === 0 ? preMeasurement : true),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-purple-100">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">
            {t('d6verification.title')}
            <span className="ml-3 text-sm font-normal text-purple-600">
              ({selectedEightD.code})
            </span>
          </h2>
          {selectedEightD.problem && (
            <div className="mt-2 text-sm text-gray-600">
              <strong>{t('d6verification.problem')}</strong> {selectedEightD.problem.line?.name} - {selectedEightD.problem.defect_type?.name}
              {selectedEightD.problem.product && ` - ${selectedEightD.problem.product.name}`}
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="text-center text-gray-500 py-4">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <p className="mt-2">{t('d6verification.loading')}</p>
        </div>
      )}

      {!loading && (
        <>
          {/* Verificar que existe el problema */}
          {!selectedEightD.problem && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800">
                {t('d6verification.noProblemAssociated')}
              </p>
            </div>
          )}

          {selectedEightD.problem && (
            <>
              {/* RESUMEN DE MEDICIONES */}
              <div className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold text-gray-800">
                {t('d6verification.measurements.title')}
              </h3>
              {preMeasurement && !showPostForm && (
                <button
                  onClick={() => setShowPostForm(true)}
                  className="px-4 py-2 bg-purple-600 text-white font-bold rounded-lg hover:bg-purple-700"
                >
                  {t('d6verification.measurements.newPost')}
                </button>
              )}
            </div>

            {/* Formulario POST */}
            {showPostForm && (
              <div className="bg-purple-50 p-4 rounded-lg mb-4">
                <h4 className="font-semibold text-gray-800 mb-3">{t('d6verification.measurements.postForm.title')}</h4>
                <p className="text-sm text-gray-600 mb-4">
                  {t('d6verification.measurements.postForm.description')}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">
                      {t('d6verification.measurements.postForm.endDate')}
                    </label>
                    <input
                      type="date"
                      value={postForm.period_end}
                      onChange={(e) => setPostForm(prev => ({ ...prev, period_end: e.target.value }))}
                      className="w-full rounded border border-gray-300 px-3 py-2"
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">
                      {t('d6verification.measurements.postForm.compareTo')}
                    </label>
                    <select
                      value={postForm.compared_to_measurement_id}
                      onChange={(e) => setPostForm(prev => ({ ...prev, compared_to_measurement_id: e.target.value }))}
                      className="w-full rounded border border-gray-300 px-3 py-2"
                    >
                      <option value="">{t('d6verification.measurements.postForm.selectReference')}</option>
                      {preMeasurement && (
                        <option value={preMeasurement.id}>
                          {t('d6verification.measurements.postForm.preRate')} {parseFloat(preMeasurement.defect_rate || 0).toFixed(4)}%
                        </option>
                      )}
                      {postMeasurements.map((m, i) => (
                        <option key={m.id} value={m.id}>
                          {t('d6verification.measurements.postForm.postRate', { index: i + 1 })} {parseFloat(m.defect_rate || 0).toFixed(4)}%
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-800 mb-1">
                    {t('d6verification.measurements.postForm.notes')}
                  </label>
                  <textarea
                    value={postForm.notes}
                    onChange={(e) => setPostForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full rounded border border-gray-300 px-3 py-2"
                    rows="3"
                    placeholder={t('d6verification.measurements.postForm.placeholder')}
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={createPostMeasurement}
                    disabled={loading}
                    className="px-4 py-2 bg-purple-600 text-white font-bold rounded-lg hover:bg-purple-700 disabled:bg-gray-300"
                  >
                    {t('d6verification.measurements.postForm.create')}
                  </button>
                  <button
                    onClick={() => setShowPostForm(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    {t('d6verification.measurements.postForm.cancel')}
                  </button>
                </div>
              </div>
            )}

            {/* Mensaje si no hay medición PRE */}
            {!preMeasurement && measurements.length === 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800">
                  {t('d6verification.measurements.noPreWarning')}
                </p>
              </div>
            )}

            {/* TARJETA PRE */}
            {preMeasurement && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="text-lg font-bold text-red-700">{t('d6verification.measurements.pre.title')}</h4>
                    <p className="text-xs text-gray-600">
                      {new Date(preMeasurement.period_start).toLocaleDateString()} → {new Date(preMeasurement.period_end).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => recalculateMeasurement(preMeasurement.id)}
                      className="px-2 py-1 bg-blue-50 text-blue-700 border border-blue-300 rounded text-xs font-semibold hover:bg-blue-100"
                      title="Recalcular con datos actuales"
                    >
                      🔄
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4 bg-white p-3 rounded border border-red-100">
                  <div className="text-center">
                    <div className="text-xs text-gray-600 mb-1">{t('d6verification.measurements.pre.goodProduction')}</div>
                    <div className="text-2xl font-bold text-gray-800">
                      {parseInt(preMeasurement.good_production || 0).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-600 mb-1">{t('d6verification.measurements.pre.defects')}</div>
                    <div className="text-2xl font-bold text-red-600">
                      {parseInt(preMeasurement.defect_count || 0).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-600 mb-1">{t('d6verification.measurements.pre.defectRate')}</div>
                    <div className="text-2xl font-bold text-red-700">
                      {parseFloat(preMeasurement.defect_rate || 0).toFixed(4)}%
                    </div>
                  </div>
                </div>

                {preMeasurement.notes && (
                  <div className="mt-2 text-xs text-gray-600 italic">
                    {preMeasurement.notes}
                  </div>
                )}
              </div>
            )}

            {/* TARJETAS POST */}
            {postMeasurements.length > 0 && (
              <div className="space-y-3">
                {postMeasurements.map((measurement, index) => {
                  const comparedTo = measurement.compared_to_measurement_id 
                    ? measurements.find(m => m.id === measurement.compared_to_measurement_id)
                    : preMeasurement;
                  
                  return (
                    <div key={measurement.id} className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="text-lg font-bold text-green-700">
                            {t('d6verification.measurements.post.title', { index: index + 1 })}
                          </h4>
                          <p className="text-xs text-gray-600">
                            {new Date(measurement.period_start).toLocaleDateString()} → {new Date(measurement.period_end).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => recalculateMeasurement(measurement.id)}
                            className="px-2 py-1 bg-blue-50 text-blue-700 border border-blue-300 rounded text-xs font-semibold hover:bg-blue-100"
                            title="Recalcular con datos actuales"
                          >
                            🔄
                          </button>
                          <button
                            onClick={() => deleteMeasurement(measurement.id)}
                            className="px-2 py-1 bg-red-50 text-red-700 border border-red-300 rounded text-xs font-semibold hover:bg-red-100"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-4 gap-4 bg-white p-3 rounded border border-green-100">
                        <div className="text-center">
                          <div className="text-xs text-gray-600 mb-1">{t('d6verification.measurements.post.goodProduction')}</div>
                          <div className="text-xl font-bold text-gray-800">
                            {parseInt(measurement.good_production || 0).toLocaleString()}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-gray-600 mb-1">{t('d6verification.measurements.post.defects')}</div>
                          <div className="text-xl font-bold text-orange-600">
                            {parseInt(measurement.defect_count || 0).toLocaleString()}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-gray-600 mb-1">{t('d6verification.measurements.post.defectRate')}</div>
                          <div className="text-xl font-bold text-green-700">
                            {parseFloat(measurement.defect_rate || 0).toFixed(4)}%
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-gray-600 mb-1">{t('d6verification.measurements.post.improvementVs', { type: comparedTo?.measurement_type || 'PRE' })}</div>
                          <div className={`text-xl font-bold ${
                            parseFloat(measurement.improvement_percentage || 0) > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {parseFloat(measurement.improvement_percentage || 0) > 0 ? '+' : ''}
                            {parseFloat(measurement.improvement_percentage || 0).toFixed(2)}%
                          </div>
                          <div className="text-xs text-gray-500">
                            {parseFloat(measurement.improvement_percentage || 0) > 0 ? t('d6verification.measurements.post.improved') : t('d6verification.measurements.post.worsened')}
                          </div>
                        </div>
                      </div>

                      {measurement.notes && (
                        <div className="mt-2 text-xs text-gray-600 italic">
                          {measurement.notes}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* GRÁFICOS */}
          {measurements.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Gráfico de Barras */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-3">{t('d6verification.charts.comparison')}</h4>
                <Bar
                  data={chartData}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: { display: false },
                      title: { display: true, text: t('d6verification.charts.defectRateLabel') }
                    },
                    scales: {
                      y: { 
                        beginAtZero: true,
                        title: { display: true, text: 'Tasa de Defectos (%)' }
                      }
                    }
                  }}
                />
              </div>

              {/* Gráfico de Tendencia */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-3">{t('d6verification.charts.trend')}</h4>
                <Line
                  data={trendChartData}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: { display: false },
                      title: { display: true, text: t('d6verification.charts.evolution') }
                    },
                    scales: {
                      y: { 
                        beginAtZero: true,
                        title: { display: true, text: 'Tasa de Defectos (%)' }
                      }
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* INTERPRETACIÓN */}
          {preMeasurement && postMeasurements.length > 0 && (
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-2">{t('d6verification.interpretation.title')}</h4>
              <div className="text-sm text-gray-700 space-y-1">
                {postMeasurements.map((post, i) => {
                  const improvement = parseFloat(post.improvement_percentage || 0);
                  const refType = post.compared_to_measurement_id === preMeasurement?.id ? 'PRE' : (language === 'es' ? 'anterior' : 'previous');
                  const message = improvement > 0 
                    ? (language === 'es' 
                        ? `Mejora del ${improvement.toFixed(2)}% respecto a la medición ${refType}`
                        : `Improvement of ${improvement.toFixed(2)}% compared to ${refType} measurement`)
                    : (language === 'es'
                        ? `Incremento del ${Math.abs(improvement).toFixed(2)}% respecto a la medición ${refType}`
                        : `Increase of ${Math.abs(improvement).toFixed(2)}% compared to ${refType} measurement`);
                  
                  return (
                    <p key={post.id}>
                      <strong>POST {i + 1}:</strong> {message}
                    </p>
                  );
                })}
                <p className="mt-2 text-xs text-gray-600">
                  {t('d6verification.interpretation.tip')}
                </p>
              </div>
            </div>
          )}

          {/* ANÁLISIS ESTADÍSTICO */}
          {preMeasurement && postMeasurements.length > 0 && (
            <div className="mt-6 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-semibold text-purple-800 text-lg flex items-center gap-2">
                  {t('d6verification.statistics.title')}
                </h4>
                <button
                  onClick={() => {
                    setShowStatistics(!showStatistics);
                    if (!showStatistics && analysisWindows.length === 0) {
                      loadAnalysisWindows();
                    }
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
                >
                  {showStatistics ? t('d6verification.statistics.hide') : t('d6verification.statistics.show')}
                </button>
              </div>

              {showStatistics && (
                <div className="space-y-6">
                  {/* Botón para crear ventana de análisis si no existe */}
                  {analysisWindows.length === 0 && (
                    <div className="text-center py-4">
                      <p className="text-gray-600 mb-3">{t('d6verification.statistics.noWindows')}</p>
                      <button
                        onClick={() => createAnalysisWindow(preMeasurement.id)}
                        disabled={loading}
                        className="px-6 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {t('d6verification.statistics.createFromPre')}
                      </button>
                    </div>
                  )}

                  {/* Mostrar ventanas de análisis existentes */}
                  {analysisWindows.map((window, idx) => (
                    <div key={window.id} className="bg-white rounded-lg p-6 shadow-md border border-purple-100">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h5 className="font-bold text-gray-800 text-lg">
                            {language === 'es' ? `Ventana de Análisis #${idx + 1}` : `Analysis Window #${idx + 1}`}
                          </h5>
                          <p className="text-xs text-gray-500">
                            {language === 'es' 
                              ? `Período PRE: ${new Date(window.pre_start).toLocaleDateString()} - ${new Date(window.pre_end).toLocaleDateString()}`
                              : `PRE Period: ${new Date(window.pre_start).toLocaleDateString()} - ${new Date(window.pre_end).toLocaleDateString()}`
                            }
                          </p>
                          {window.post_start && (
                            <p className="text-xs text-gray-500">
                              {language === 'es'
                                ? `Período POST: ${new Date(window.post_start).toLocaleDateString()} - ${new Date(window.post_end).toLocaleDateString()}`
                                : `POST Period: ${new Date(window.post_start).toLocaleDateString()} - ${new Date(window.post_end).toLocaleDateString()}`
                              }
                            </p>
                          )}
                        </div>
                        {!window.post_mean && postMeasurements.length > 0 && (
                          <button
                            onClick={() => updateWindowWithPost(window.id, postMeasurements[0].id)}
                            disabled={loading}
                            className="px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
                          >
                            {t('d6verification.statistics.window.runTests')}
                          </button>
                        )}
                      </div>

                      {/* Estadísticas Descriptivas */}
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-red-50 p-4 rounded border border-red-200">
                          <h6 className="font-semibold text-red-800 mb-2">{t('d6verification.statistics.descriptive.pre.title')}</h6>
                          <div className="space-y-1 text-sm">
                            <p><strong>{t('d6verification.statistics.descriptive.pre.mean')}</strong> {parseFloat(window.pre_mean || 0).toFixed(4)}%</p>
                            <p><strong>{t('d6verification.statistics.descriptive.pre.stdDev')}</strong> {parseFloat(window.pre_std_dev || 0).toFixed(4)}%</p>
                            <p><strong>{t('d6verification.statistics.descriptive.pre.sampleSize')}</strong> {window.pre_sample_size} días</p>
                          </div>
                        </div>

                        {window.post_mean && (
                          <div className="bg-green-50 p-4 rounded border border-green-200">
                            <h6 className="font-semibold text-green-800 mb-2">{t('d6verification.statistics.descriptive.post.title')}</h6>
                            <div className="space-y-1 text-sm">
                              <p><strong>{t('d6verification.statistics.descriptive.post.mean')}</strong> {parseFloat(window.post_mean || 0).toFixed(4)}%</p>
                              <p><strong>{t('d6verification.statistics.descriptive.post.stdDev')}</strong> {parseFloat(window.post_std_dev || 0).toFixed(4)}%</p>
                              <p><strong>{t('d6verification.statistics.descriptive.post.sampleSize')}</strong> {window.post_sample_size} días</p>
                              <p className={`font-bold ${parseFloat(window.improvement_percentage || 0) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                <strong>{t('d6verification.statistics.descriptive.post.improvement')}</strong> {parseFloat(window.improvement_percentage || 0) > 0 ? '+' : ''}{parseFloat(window.improvement_percentage || 0).toFixed(2)}%
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Resultados de Tests Estadísticos */}
                      {window.stat_tests && window.stat_tests.length > 0 && (
                        <div className="mt-4">
                          <h6 className="font-semibold text-gray-800 mb-3">{t('d6verification.statistics.tests.title')}</h6>
                          <div className="space-y-3">
                            {window.stat_tests.map((test, testIdx) => (
                              <div key={testIdx} className={`p-4 rounded border-2 ${test.significant ? 'bg-green-50 border-green-300' : 'bg-yellow-50 border-yellow-300'}`}>
                                <div className="flex justify-between items-start mb-2">
                                  <div>
                                    <h6 className="font-semibold text-gray-800">
                                      {test.method.replace(/_/g, ' ').toUpperCase()}
                                    </h6>
                                    {test.method !== 'power_analysis' && (
                                      <p className="text-sm text-gray-600">
                                        {t('d6verification.statistics.tests.pValue')} <span className="font-mono font-bold">{parseFloat(test.p_value || 0).toFixed(6)}</span>
                                      </p>
                                    )}
                                  </div>
                                  <div className={`px-3 py-1 rounded-full text-xs font-bold ${test.significant ? 'bg-green-600 text-white' : 'bg-yellow-600 text-white'}`}>
                                    {test.significant ? t('d6verification.statistics.tests.significant') : t('d6verification.statistics.tests.notSignificant')}
                                  </div>
                                </div>
                                
                                {test.summary && test.summary.interpretation && (
                                  <p className="text-sm text-gray-700 mt-2">
                                    {test.summary.interpretation}
                                  </p>
                                )}

                                {/* Detalles específicos del test */}
                                {test.method === 't_test_two_sample' && test.summary && (
                                  <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                                    <div className="bg-white p-2 rounded">
                                      <p className="text-gray-600">{t('d6verification.statistics.tests.tStatistic')}</p>
                                      <p className="font-bold">{test.summary.t_statistic}</p>
                                    </div>
                                    <div className="bg-white p-2 rounded">
                                      <p className="text-gray-600">{t('d6verification.statistics.tests.degreesOfFreedom')}</p>
                                      <p className="font-bold">{test.summary.degrees_of_freedom}</p>
                                    </div>
                                    <div className="bg-white p-2 rounded">
                                      <p className="text-gray-600">{t('d6verification.statistics.tests.differenceMeans')}</p>
                                      <p className="font-bold">{test.summary.difference_means?.toFixed(4)}</p>
                                    </div>
                                  </div>
                                )}

                                {test.method === 'anova_one_way' && test.summary && (
                                  <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                                    <div className="bg-white p-2 rounded">
                                      <p className="text-gray-600">{t('d6verification.statistics.tests.fStatistic')}</p>
                                      <p className="font-bold">{test.summary.f_statistic}</p>
                                    </div>
                                    <div className="bg-white p-2 rounded">
                                      <p className="text-gray-600">{t('d6verification.statistics.tests.dfBetween')}</p>
                                      <p className="font-bold">{test.summary.df_between}</p>
                                    </div>
                                    <div className="bg-white p-2 rounded">
                                      <p className="text-gray-600">{t('d6verification.statistics.tests.dfWithin')}</p>
                                      <p className="font-bold">{test.summary.df_within}</p>
                                    </div>
                                  </div>
                                )}

                                {test.method === 'power_analysis' && test.summary && (
                                  <div className="mt-2">
                                    <div className="bg-white p-3 rounded">
                                      <p className="text-sm"><strong>{t('d6verification.statistics.tests.power')}:</strong> {(test.summary.power * 100).toFixed(1)}%</p>
                                      <div className="w-full bg-gray-200 rounded-full h-3 mt-2">
                                        <div 
                                          className={`h-3 rounded-full ${test.summary.power >= 0.8 ? 'bg-green-500' : 'bg-yellow-500'}`}
                                          style={{ width: `${test.summary.power * 100}%` }}
                                        ></div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Resumen y Conclusión */}
                      {statisticalSummary && statisticalSummary.window_id === window.id && (
                        <div className="mt-4 bg-indigo-50 border border-indigo-300 rounded-lg p-4">
                          <h6 className="font-bold text-indigo-900 mb-2">{t('d6verification.statistics.conclusion.title')}</h6>
                          <p className="text-sm text-gray-800 font-semibold mb-2">
                            {translateConclusion(statisticalSummary.conclusion)}
                          </p>
                          <p className="text-xs text-gray-600">
                            {statisticalSummary.significant_tests} {t('d6verification.statistics.conclusion.of')} {statisticalSummary.tests_performed} {t('d6verification.statistics.conclusion.significantTests')} (α = 0.05)
                          </p>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Información sobre Tests Estadísticos */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h6 className="font-semibold text-blue-800 mb-2">{t('d6verification.statistics.info.title')}</h6>
                    <div className="text-xs text-gray-700 space-y-1">
                      <p><strong>{t('d6verification.statistics.info.tTest')}</strong></p>
                      <p><strong>{t('d6verification.statistics.info.zTest')}</strong></p>
                      <p><strong>{t('d6verification.statistics.info.anova')}</strong></p>
                      <p><strong>{t('d6verification.statistics.info.power')}</strong></p>
                      <p className="mt-2 text-blue-700"><strong>{t('d6verification.statistics.info.alpha')}</strong></p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default D6Verification;
